/**
 * Product price on a dealer self sign-up link.
 *
 * Assumed API contract for frankhustle/priorityone_backend (align field names):
 *
 * Writes — POST v1/partner/generate-signup-link
 *          PUT  v1/selfsignup/:id
 *   priceOverride: number | null
 *     Dollars. A positive amount used only for this link.
 *     null means there is no manual override; the link uses the merchant price.
 *
 * Reads — each signup link from GET v1/selfsignup/:partnerCompanyId
 *   priceOverride: number | null   manual amount, when one has been saved
 *   merchantPrice: number | null   dealer price for this link's product
 *   effectivePrice: number | null  priceOverride when set, otherwise merchantPrice
 *
 * The create/edit form also reads the dealer price from products already loaded
 * for the company (GET v1/pricing/company/:id?getRRP=true), preferring
 * `merchantPrice` then `partnerPricing`. GET v1/partner/:id/pricing (Available
 * Pricing Options) is merged in as `merchantPrice` when the product payload
 * does not already include one. Catalog `product.price` is only a last-resort
 * display fallback and is never treated as the dealer price.
 */

export function coerceMoney(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? roundMoney(value) : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || !/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? roundMoney(n) : null;
  }
  if (typeof value === "object") {
    if ("price" in value) return coerceMoney(value.price);
    if ("amount" in value) return coerceMoney(value.amount);
  }
  return null;
}

function roundMoney(n) {
  return Math.round(n * 100) / 100;
}

function nonNegativeMoney(value) {
  const n = coerceMoney(value);
  return n != null && n >= 0 ? n : null;
}

export function formatMoney(amount) {
  if (amount == null || !Number.isFinite(Number(amount))) return null;
  return Number(amount).toFixed(2);
}

/** Dealer/merchant price on a company pricing product. Ignores catalog `price`. */
export function merchantPriceFromProduct(product) {
  if (!product || typeof product !== "object") return null;
  const explicit = nonNegativeMoney(product.merchantPrice);
  if (explicit != null) return explicit;
  const partner = nonNegativeMoney(product.partnerPricing);
  if (partner != null) return partner;
  const partnerPrice = nonNegativeMoney(product.partnerPrice);
  if (partnerPrice != null) return partnerPrice;
  const dealerPrice = nonNegativeMoney(product.dealerPrice);
  if (dealerPrice != null) return dealerPrice;
  return null;
}

export function catalogPriceFromProduct(product) {
  if (!product || typeof product !== "object") return null;
  return nonNegativeMoney(product.price);
}

function productIdOf(value) {
  if (value == null || value === "") return null;
  if (typeof value === "object") return value._id != null ? String(value._id) : null;
  return String(value);
}

export function linkProductIds(link) {
  if (!link) return [];
  return [productIdOf(link.idProduct), productIdOf(link.idSubscription)].filter(Boolean);
}

export function productMatchesLink(link, product) {
  if (!link || !product || product._id == null) return false;
  return linkProductIds(link).includes(String(product._id));
}

function findPartnerProduct(link, partnerProducts) {
  if (!Array.isArray(partnerProducts)) return null;
  return partnerProducts.find((product) => productMatchesLink(link, product)) || null;
}

function populatedLinkProduct(link) {
  if (link?.idSubscription && typeof link.idSubscription === "object") return link.idSubscription;
  if (link?.idProduct && typeof link.idProduct === "object") return link.idProduct;
  return null;
}

/** Manual override stored on the link. Zero and empty are "no override". */
export function readPriceOverride(link) {
  if (!link || link.priceOverride == null || link.priceOverride === "") return null;
  const n = coerceMoney(link.priceOverride);
  return n != null && n > 0 ? n : null;
}

/**
 * Overlay Available Pricing Option amounts onto company products.
 * An explicit `merchantPrice` already on the product is left as-is.
 */
export function applyDealerPrices(products, pricingOptions) {
  const list = Array.isArray(products) ? products : [];
  const byId = new Map();
  (Array.isArray(pricingOptions) ? pricingOptions : []).forEach((option) => {
    const productId = productIdOf(option?.product);
    const price = nonNegativeMoney(option?.price);
    if (productId == null || price == null) return;
    byId.set(productId, price);
  });

  return list.map((product) => {
    if (!product) return product;
    if (nonNegativeMoney(product.merchantPrice) != null) return product;
    const dealer = byId.get(productIdOf(product));
    if (dealer == null) return product;
    return { ...product, merchantPrice: dealer };
  });
}

export function dealerPriceForSelection(product, link) {
  if (!product) return null;
  if (link && productMatchesLink(link, product)) {
    const fromLink = nonNegativeMoney(link.merchantPrice);
    if (fromLink != null) return fromLink;
  }
  return merchantPriceFromProduct(product);
}

/**
 * Strict parser for the Product Price input.
 * Blank means "use the dealer default" (`priceOverride: null`).
 */
export function parsePriceOverrideInput(raw) {
  if (raw == null || String(raw).trim() === "") {
    return { ok: true, value: null };
  }
  const trimmed = String(raw).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return {
      ok: false,
      value: null,
      error: "Product price must be a positive number with at most 2 decimal places."
    };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) {
    return {
      ok: false,
      value: null,
      error: "Product price must be a positive number."
    };
  }
  return { ok: true, value: roundMoney(n) };
}

export function productPriceFieldCopy({ dealerPrice, catalogPrice, overrideValue }) {
  const parsed = parsePriceOverrideInput(overrideValue);
  const incomplete =
    overrideValue != null && /^\d+\.$/.test(String(overrideValue).trim());
  const dealerLabel =
    dealerPrice != null ? `Defaults to dealer price $${formatMoney(dealerPrice)}` : null;
  const catalogLabel =
    catalogPrice != null ? `catalog price $${formatMoney(catalogPrice)}` : null;

  let placeholder = "Enter a product price";
  let helper = "Select a product to see the dealer price.";

  if (dealerLabel) {
    placeholder = dealerLabel;
    helper = `${dealerLabel}. Leave blank to use that price, or enter an amount for this link only.`;
  } else if (catalogLabel) {
    placeholder = `Defaults to ${catalogLabel}`;
    helper = `No dealer price is set for this product. Leave blank to use the ${catalogLabel}.`;
  }

  if (!incomplete && parsed.ok && parsed.value != null) {
    helper = dealerLabel
      ? `${dealerLabel}. This link will use $${formatMoney(parsed.value)}.`
      : `This link will use $${formatMoney(parsed.value)}.`;
  }

  return {
    placeholder,
    helper,
    error: incomplete || parsed.ok ? null : parsed.error
  };
}

export function resolveSignupLinkPrices(link, partnerProducts = []) {
  const partnerProduct = findPartnerProduct(link, partnerProducts);
  const populated = populatedLinkProduct(link);

  const merchantPrice =
    nonNegativeMoney(link?.merchantPrice) ??
    merchantPriceFromProduct(partnerProduct) ??
    merchantPriceFromProduct(populated);

  const priceOverride = readPriceOverride(link);
  const effectiveFromApi = nonNegativeMoney(link?.effectivePrice);

  const effectivePrice =
    priceOverride ??
    effectiveFromApi ??
    merchantPrice ??
    catalogPriceFromProduct(populated) ??
    catalogPriceFromProduct(partnerProduct);

  return { merchantPrice, priceOverride, effectivePrice };
}
